import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import InfiniteScroll from 'react-infinite-scroll-component';

const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:8000';

const Basic3DIconsList = () => {
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Reference to container for checking height
  const containerRef = useRef(null);

  const LIMIT = 50;

  const fetchIcons = async (currentPage) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/icons_list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          collection: 'basic3d',
          media_type: 'rastericon',
          page: currentPage,
          limit: LIMIT,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();

      if (currentPage === 1) {
        setIcons(data.icons);
      } else {
        setIcons((prevIcons) => [...prevIcons, ...data.icons]);
      }

      const totalPages = Math.ceil(data.total / data.limit);
      if (currentPage >= totalPages) {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error fetching icons:', err);
      setError('Failed to load icons.');
      setHasMore(false); // Останавливаем попытки при ошибке
    } finally {
      setLoading(false);
    }
  };

  // Initial loading of first page of icons
  useEffect(() => {
    fetchIcons(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMoreIcons = () => {
    const nextPage = page + 1;
    fetchIcons(nextPage);
    setPage(nextPage);
  };

  // Check viewport filling after icons update
  useEffect(() => {
    if (!loading && hasMore && !error) { // Добавлена проверка !error
      const containerHeight = containerRef.current?.clientHeight || 0;
      const windowHeight = window.innerHeight;

      // If container height is less than window height, load more
      if (containerHeight < windowHeight) {
        fetchMoreIcons();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [icons, loading, hasMore, error]); // Добавлен error в зависимости

  return (
    <div className="w-full py-8" ref={containerRef}>
      <style jsx>{`
        .basic3d-icon {
          max-width: 65% !important;
          max-height: 65% !important;
          width: 65% !important;
          height: 65% !important;
          transform: scale(1.3);
        }
        .basic3d-container {
          padding: 0 !important;
        }
        .icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>

      <h1 className="text-3xl font-bold mb-6">Basic 3D Icons</h1>

      {error && <div className="text-red-500 mb-4">{error}</div>}

      {loading && icons.length === 0 ? (
        <div className="text-gray-500 text-center py-8">Loading icons...</div>
      ) : (
        <InfiniteScroll
          dataLength={icons.length}
          next={fetchMoreIcons}
          hasMore={hasMore}
          loader={<div className="text-gray-500 text-center py-4">Loading more icons...</div>}
          endMessage={
            !error && (
              <p className="text-center text-gray-500 py-4">
                All icons have been loaded.
              </p>
            )
          }
        >
          <div className="mb-8">
            <div className="grid grid-cols-auto-fill gap-2 w-full mx-auto" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
              {icons.map((icon) => (
                <div key={icon.id}>
                  <Link href={`/basic3d/${icon.slug}`} title={icon.title}>
                    <div className="icon-container basic3d-container flex items-center justify-center border border-gray-200 rounded-lg aspect-square group">
                      <img
                        src={`https://icons-pbl.davooda.com/basic3d-icons/png-256px/${icon.slug}.png`}
                        alt={icon.title || icon.slug}
                        className="basic3d-icon transition-transform duration-300 group-hover:scale-110"
                        style={{
                          maxWidth: '65%',
                          maxHeight: '65%',
                          width: '65%',
                          height: '65%'
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = icon.url || `/placeholder-icon.png`;
                        }}
                      />
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </InfiniteScroll>
      )}

      {!loading && !error && icons.length === 0 && (
        <div className="text-gray-500 text-center py-8">No icons found.</div>
      )}
    </div>
  );
};
 
export default Basic3DIconsList;